import { useCallback, useState } from 'react'

const STORAGE_PREFIX = 'sidebarProjectsCollapsed.'

const readStored = (lifeSpace: string): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + lifeSpace)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

const writeStored = (lifeSpace: string, collapsed: ReadonlySet<string>): void => {
  try {
    localStorage.setItem(STORAGE_PREFIX + lifeSpace, JSON.stringify([...collapsed]))
  } catch {
    /* private mode / storage disabled — the branch still toggles for this session */
  }
}

/**
 * **Welche Projekte ihre Unterprojekte zugeklappt haben** (#185) — je Lebensbereich, auf
 * jedem Gerät für sich (`localStorage`, wie die Gruppen der Sidebar in `SidebarSection`),
 * nicht im Sync. Standard: offen; gemerkt werden nur die zugeklappten.
 *
 * Gelesen beim Einhängen: `StandardSidebar` hängt den Inhalt bei jedem Wechsel des
 * Lebensbereichs neu ein.
 */
export function useProjectExpand(lifeSpace: string) {
  const [collapsed, setCollapsedState] = useState<ReadonlySet<string>>(() => readStored(lifeSpace))

  const update = useCallback((change: (prev: ReadonlySet<string>) => ReadonlySet<string>) => {
    setCollapsedState(prev => {
      const next = change(prev)
      if (next !== prev) writeStored(lifeSpace, next)
      return next
    })
  }, [lifeSpace])

  const toggleProjectExpand = useCallback((projectId: string) => {
    update(prev => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }, [update])

  /** Klappt diese Projekte auf (Vorfahren des aktiven Projekts, Ziel eines Ablegens). */
  const expandProjects = useCallback((projectIds: readonly string[]) => {
    update(prev => {
      if (!projectIds.some(id => prev.has(id))) return prev
      const next = new Set(prev)
      for (const id of projectIds) next.delete(id)
      return next
    })
  }, [update])

  return { collapsedProjects: collapsed, toggleProjectExpand, expandProjects }
}
